/*
 * "Pulso": una fila por zona con sus indicadores clave (IGI, conteo de 3 colores, visitas, acciones
 * pendientes, completitud, alertas, nivel) mas un grafico de barras (react-google-charts, ya usado
 * en pages/admin/statistics) comparando el IGI promedio entre zonas, y dos tarjetas ("Top 3 zonas" y
 * "zonas que necesitan atencion") calculadas aqui mismo ordenando las zonas ya traidas por
 * igi_promedio. Solo para quien ve todo el territorio (igi_view_all): el jefe zonal no la ve (usa
 * "Mi zona"). Reutiliza GET /igi/resumen/, sin endpoint propio. Mismo patron pedido/cargando que
 * mi_zona/index.js (Task 7 de la etapa 2). "Ver zona" navega a /igi/mi-zona?zona=<id>, que esa
 * pantalla lee para preseleccionar la zona.
 */
import React, {useCallback, useEffect, useState} from "react";
import {Chart} from "react-google-charts";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import Breadcrumbs from "../../../../components/breadcrumbs";
import {clear_igi, get_igi_resumen, GET_IGI_RESUMEN_SUCCESS} from "../../../../redux/igi";
import {NivelBadge, formatoNota} from "../niveles";

const FECHA_HOY = () => new Date().toLocaleDateString("es-EC", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
});

const DistribucionBar = ({conteo}) => {
    const total = conteo.fuerte + conteo.media + conteo.debil + conteo.sin_igi;
    if (total === 0) {
        return <div className="small text-muted">Sin datos</div>;
    }
    const segmento = (cantidad, color) => (
        cantidad > 0
            ? <div style={{width: `${(cantidad / total) * 100}%`, background: color}}/>
            : null
    );
    return (
        <div className="d-flex" style={{height: "8px", borderRadius: "4px", overflow: "hidden", background: "#eee"}}>
            {segmento(conteo.fuerte, "#28c76f")}
            {segmento(conteo.media, "#ff9f43")}
            {segmento(conteo.debil, "#ea5455")}
            {segmento(conteo.sin_igi, "#d7d7d7")}
        </div>
    );
};

const Pulso = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    const resumen = useSelector((state) => state.igi_module._get_resumen);
    const [pedido, setPedido] = useState(false);

    const recargar = useCallback(() => {
        setPedido(true);
        dispatch(get_igi_resumen(token));
    }, [dispatch, token]);

    useEffect(() => {
        dispatch(clear_igi(GET_IGI_RESUMEN_SUCCESS));
        recargar();
    }, [dispatch, recargar]);

    const cargando = pedido && resumen.status === 0;
    const error = resumen.status !== 0 && resumen.status !== 200
        ? (resumen.message || "No se pudo cargar el pulso del territorio") : null;
    const zonas = resumen.data && resumen.data.datos ? resumen.data.datos : [];

    // Peor a mejor no sirve para "Top 3"/"Atencion": se ordena de mejor a peor una sola vez y se toma
    // de los extremos (las sin igi_promedio quedan al final, ninguna de las dos tarjetas las usa).
    const zonasOrdenadas = [...zonas].sort((a, b) => (b.igi_promedio ?? -1) - (a.igi_promedio ?? -1));
    const conIgi = zonasOrdenadas.filter((z) => z.igi_promedio !== null);
    const top3 = conIgi.slice(0, 3);
    const atencion = [...conIgi].slice(-3).reverse();

    const datosGrafico = [
        ["Zona", "IGI promedio"],
        ...zonas.map((z) => [z.nombre, z.igi_promedio || 0]),
    ];

    const verZona = (zonaId) => history.push(`/igi/mi-zona?zona=${zonaId}`);

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1">
                        <Breadcrumbs title="Pulso" items={[]}/>
                        <h6 className="text-muted text-capitalize mt-50">Pulso Semanal — {FECHA_HOY()}</h6>
                    </div>
                </div>
                <div className="content-body">
                    {cargando && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando pulso del territorio…
                        </div>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}
                    {zonas.length > 0 && (
                        <div className="card mb-1">
                            <div className="card-body">
                                <Chart
                                    chartType="ColumnChart"
                                    data={datosGrafico}
                                    width="100%"
                                    height="320px"
                                    options={{legend: {position: "none"}, vAxis: {title: "IGI promedio"}}}
                                />
                            </div>
                        </div>
                    )}
                    {zonas.length === 0 && !cargando && !error && (
                        <div className="alert alert-info">Sin zonas visibles.</div>
                    )}
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Zona</th>
                                    <th>Líder</th>
                                    <th className="text-center">IGI</th>
                                    <th className="text-center">🟢</th>
                                    <th className="text-center">🟡</th>
                                    <th className="text-center">🔴</th>
                                    <th>Distribución</th>
                                    <th className="text-center">Visitas</th>
                                    <th className="text-center">Acc. Pend.</th>
                                    <th className="text-center">Completitud</th>
                                    <th>Estado</th>
                                    <th/>
                                </tr>
                            </thead>
                            <tbody>
                                {zonasOrdenadas.map((z) => (
                                    <tr key={z.id}>
                                        <td><strong>{z.nombre}</strong></td>
                                        <td>{z.jefe.nombres || z.jefe.usuario}</td>
                                        <td className="text-center">{formatoNota(z.igi_promedio)}</td>
                                        <td className="text-center">{z.conteo.fuerte}</td>
                                        <td className="text-center">{z.conteo.media}</td>
                                        <td className="text-center">{z.conteo.debil}</td>
                                        <td style={{minWidth: "100px"}}><DistribucionBar conteo={z.conteo}/></td>
                                        <td className="text-center">{z.visitas}</td>
                                        <td className="text-center">{z.acciones_pendientes}</td>
                                        <td className="text-center">{z.completitud}%</td>
                                        <td><NivelBadge nivel={z.nivel_zona}/></td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={() => verZona(z.id)}
                                            >
                                                <i className="bx bx-show"/> Ver zona
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {(top3.length > 0 || atencion.length > 0) && (
                        <div className="row">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-body">
                                        <h6><i className="bx bx-trophy text-warning"/> Top 3 zonas esta semana</h6>
                                        {top3.map((z, i) => (
                                            <div key={z.id} className="d-flex justify-content-between align-items-center py-50 border-bottom">
                                                <span>{["🥇", "🥈", "🥉"][i]} <strong>{z.nombre}</strong>{" "}
                                                    <span className="text-muted small">{z.jefe.nombres || z.jefe.usuario}</span>
                                                </span>
                                                <span className="text-success font-weight-bold">{formatoNota(z.igi_promedio)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-body">
                                        <h6><i className="bx bx-error text-danger"/> Zonas que necesitan atención</h6>
                                        {atencion.map((z) => (
                                            <div key={z.id} className="d-flex justify-content-between align-items-center py-50 border-bottom">
                                                <span>🔴 <strong>{z.nombre}</strong>{" "}
                                                    <span className="text-muted small">{z.jefe.nombres || z.jefe.usuario}</span>
                                                </span>
                                                <span className="text-danger font-weight-bold">{formatoNota(z.igi_promedio)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Pulso;
